import { useId } from '@reach/auto-id';
import React, { useContext, useEffect, useRef, } from 'react';
import FocusLock from 'react-focus-lock';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import * as icons from '@boxine/tonies-ui/icons';
import { AssistiveTechnologyOnly } from '../../../styles/AssistiveTechnologyOnly';
import { NoWordWrap } from '../../../styles/NoWordWrap';
import Icon from '../../Icon';
import { HORIZONTAL_DISTANCE_NAV_ITEMS_VIEWPORT } from '../constants';
import { isRouteMatch } from '../helpers/isRouteMatch';
import { useIsMobileNavigation } from '../hooks/useIsMobileNavigation';
import { useNavigationItemHandler } from '../hooks/useNavigationItemHandler';
import { MainNavigationContext } from '../MainNavigationContext';
import { FocusLink } from '../../FocusLink';
import { ButtonLink } from './ButtonLink';
import { NestedNavigation } from './NestedNavigation';
import { NestedNavigationItem } from './NestedNavigationItem';
import { Tag } from './Tag';
const PositionedTag = styled(Tag) `
  position: absolute;
  text-transform: uppercase;
  top: -1rem;

  ${props => props.isMobile
    ? css `
          transform: translateX(-1rem);
        `
    : css `
          top: -1.1rem;
          right: -1rem;
        `}
`;
const NoWrapLink = styled(FocusLink) `
  position: relative;

  ${props => props.isLinkActive &&
    css `
      color: ${props => props.theme.BrandPrimary};
    `};

  ${props => props.isFullWidth &&
    css `
      display: flex;
      justify-content: center;
      margin-right: ${HORIZONTAL_DISTANCE_NAV_ITEMS_VIEWPORT};
      margin-left: ${HORIZONTAL_DISTANCE_NAV_ITEMS_VIEWPORT};
    `};

  ${NoWordWrap};

  /* Required for very large text */
  line-height: normal;

  &:hover {
    color: ${props => props.theme.BrandPrimary};
  }
`;
const VisuallyHiddenText = styled.span `
  ${AssistiveTechnologyOnly};
`;
const StyledButtonLink = styled(ButtonLink) `
  color: ${props => props.theme.Gray};
  display: block;
  /* Reference point for arrow */
  position: relative;
  margin-right: ${HORIZONTAL_DISTANCE_NAV_ITEMS_VIEWPORT};
  margin-left: ${HORIZONTAL_DISTANCE_NAV_ITEMS_VIEWPORT};
  width: calc(100% - 2 * ${HORIZONTAL_DISTANCE_NAV_ITEMS_VIEWPORT});
`;
const StyledIcon = styled(Icon) `
  height: 1.5rem;
  width: 1.5rem;
  position: absolute;
  right: 0;
`;
/**
 * The active state of this component can be set in 2 ways: Using the isActive
 * prop (required for the shop integration) or relying on the internal route
 * match logic of ReactRouter component without setting any prop manually.
 */
export const PrimaryNavigationItem = ({ children = null, hasNewLabel = false, isActive = false, nestedNavigationItems = [], tagText = undefined, to, onClick = undefined,
// eslint-disable-next-line sonarjs/cognitive-complexity
 }) => {
    const { t } = useTranslation('MainNavigation');
    const linkRef = useRef(null);
    const { isMobileMenuOpen, isNestedNavigationOpen, setIsNestedNavigationOpen, setShouldFocusNestedNavigation, shouldFocusNestedNavigation, } = useContext(MainNavigationContext);
    const isMobileNavigation = useIsMobileNavigation();
    const nestedNavigationId = useId();
    const isLinkActive = isActive || isRouteMatch(to);
    const tagContent = tagText ? tagText : hasNewLabel ? t('NewLabel') : undefined;
    // Hook to register, unregister and update the item
    useNavigationItemHandler(linkRef, isLinkActive);
    useEffect(() => {
        if (isLinkActive &&
            nestedNavigationItems.length > 0 &&
            isMobileMenuOpen &&
            !isNestedNavigationOpen &&
            shouldFocusNestedNavigation &&
            linkRef &&
            linkRef.current) {
            linkRef.current.focus();
            linkRef.current.click();
            setShouldFocusNestedNavigation(false);
        }
    }, [
        isLinkActive,
        isMobileMenuOpen,
        isNestedNavigationOpen,
        nestedNavigationItems,
        setIsNestedNavigationOpen,
        setShouldFocusNestedNavigation,
        shouldFocusNestedNavigation,
    ]);
    function renderItem() {
        return (React.createElement(NoWrapLink, { isFullWidth: isMobileNavigation, isLinkActive: isLinkActive, ref: linkRef, to: to, onClick: onClick },
            React.createElement("span", null,
                children,
                tagContent && (React.createElement(PositionedTag, { isMobile: isMobileNavigation }, tagContent)))));
    }
    if (nestedNavigationItems && nestedNavigationItems.length > 0) {
        if (isMobileNavigation) {
            return (React.createElement(React.Fragment, null,
                React.createElement(StyledButtonLink, { ariaControls: nestedNavigationId, ariaExpanded: isNestedNavigationOpen, onClick: () => setIsNestedNavigationOpen(!isNestedNavigationOpen), ref: linkRef },
                    children,
                    React.createElement(VisuallyHiddenText, null, t('Submenu')),
                    React.createElement(StyledIcon, { type: icons.arrowRight, title: null })),
                React.createElement(FocusLock, { disabled: !isNestedNavigationOpen, returnFocus: true, autoFocus: true },
                    React.createElement(NestedNavigation, { id: nestedNavigationId, isOpen: isNestedNavigationOpen, onClose: () => setIsNestedNavigationOpen(false) }, nestedNavigationItems.map(({ isActive, label, target }) => (React.createElement(NestedNavigationItem, { isActive: isActive, key: target, target: target }, label)))))));
        }
        else {
            return (React.createElement(React.Fragment, null,
                renderItem(),
                isLinkActive && (React.createElement(NestedNavigation, null, nestedNavigationItems.map(({ isActive, label, target }, index) => (React.createElement(NestedNavigationItem, { isActive: isActive, key: index, target: target }, label)))))));
        }
    }
    else {
        return renderItem();
    }
};
